import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { colorsTheme } from 'resources/theme/styled/colors';
import { generateUniqId } from 'helpers/commonHelpers';
import { RelationshipTemplate } from 'typings/application/relationship-template';
import { SubmitButtonsFormValues } from '../SubmitButtons';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';
import { formatFromJSON, formatToJSON } from 'helpers/crmHelpers';
import { basicInfoStepValidationRules } from 'validations/intake-form/intake-form-steps';
import { ApplicantRelationshipFromQuery } from 'api/crm/intakeFormAPI';
import { AdditionalFieldFormItemWithNullableIdModel } from 'components/Forms/FormComponents/AdditionalField';
import {
  AdditionalFieldTypes,
  HearAboutTenetVariants,
} from 'enums/onboarding/crm';

// components
import InnerForm from './InnerForm';
import DivAlignCenter from 'components/Additional/DivAlignCenter';
import SectionTitleNavigateButton from '../SectionTitleNavigateButton';
import { Form, SectionIntro } from '@ui';

export type RelationshipModel = {
  id: string;
  template: string | null;
  additionalFields: AdditionalFieldFormItemWithNullableIdModel[];
};

export interface FormValuesModel extends SubmitButtonsFormValues {
  legalName: string;
  tradeName: string;
  hearTenet: HearAboutTenetVariants | null;
  referrerName: string;
  referrerCompany: string;
  referrerInformation: string;
  relationships: RelationshipModel[];
}

interface IProps {
  disabled?: boolean;
  renderNavigateButtonInTitle?: boolean;
}

const BasicInfo = ({ disabled, renderNavigateButtonInTitle }: IProps) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const {
    intakeForm,
    navigateToNextStep,
    navigateToPreviousStep,
    updateIntakeFormFields,
  } = useEntityIntakeFormContext();

  const initialValues = useMemo<FormValuesModel>(() => {
    const formatAdditionalFields = (
      relationship: ApplicantRelationshipFromQuery,
      relationshipTemplates: RelationshipTemplate[],
    ): AdditionalFieldFormItemWithNullableIdModel[] => {
      const template = relationshipTemplates.find(
        (e) => e._id === relationship.relationshipTemplateId,
      );
      if (!template) {
        return [];
      }

      return template.additionalFields
        .filter(
          ({ relatedTo }) => relatedTo === 'any' || relatedTo === 'contact',
        )
        .map((e) => {
          const predefinedAF = relationship.additionalFields.find(
            (rAF) => rAF.name === e.name,
          );

          let formattedValue = predefinedAF
            ? formatFromJSON(e.type, predefinedAF.valueJSON)
            : undefined;

          if (
            [AdditionalFieldTypes.Form, AdditionalFieldTypes.Document].includes(
              e.type,
            ) &&
            formattedValue
          ) {
            if (formattedValue && Array.isArray(formattedValue)) {
              formattedValue = formattedValue.map((e) => {
                const document = intakeForm && intakeForm.metadata.documents[e];
                if (document) {
                  return {
                    id: document.id,
                    name: document.files[0].name,
                    fileId: document.files[0].id,
                    file: document.files[0],
                  };
                }
              });
            }
          }

          return {
            id: predefinedAF?.id || null,
            name: e.name,
            type: e.type,
            value: formattedValue,
            relatedTo: e.relatedTo,
            options: {
              onboardingRequired: e.options.onboardingRequired,
              isForRegulatedOnly: e.options.isForRegulatedOnly,
              documentRequiresCertification:
                e.options.documentRequiresCertification,
              selectOptions: e.options.selectOptions.length
                ? e.options.selectOptions.map((e) => JSON.parse(e.valueJSON))
                : [],
              formDocument: e.options.formDocument,
            },
          };
        });
    };

    return {
      submitType: null,
      legalName: intakeForm?.fields?.entityLegalName || '',
      tradeName: intakeForm?.fields?.entityDBA || '',
      hearTenet: intakeForm?.fields?.hearTenet || null,
      referrerName: intakeForm?.fields?.referrerName || '',
      referrerCompany: intakeForm?.fields?.referrerCompany || '',
      referrerInformation: intakeForm?.fields?.referrerMore || '',
      relationships:
        Array.isArray(intakeForm?.fields?.applicantRelations) &&
        intakeForm.fields.applicantRelations.length
          ? intakeForm.fields.applicantRelations.map((e) => ({
              id: e.id || `custom-${generateUniqId()}`,
              template: e.relationshipTemplateId || '',
              additionalFields: formatAdditionalFields(
                e,
                intakeForm.metadata.relationshipTemplates,
              ),
            }))
          : [
              {
                id: `custom-${generateUniqId()}`,
                template: null,
                additionalFields: [],
              },
            ],
    };
  }, [intakeForm]);

  const handleSubmit = async (values: FormValuesModel) => {
    if (!intakeForm) {
      return;
    }
    const filledRelationships = values.relationships.filter(
      (e) => !!e.template,
    );
    await updateIntakeFormFields(
      {
        entityLegalName: values.legalName || null,
        entityDBA: values.tradeName || null,
        hearTenet: values.hearTenet || null,
        referrerName:
          values.hearTenet === HearAboutTenetVariants.Referrer
            ? values.referrerName || null
            : null,
        referrerCompany:
          values.hearTenet === HearAboutTenetVariants.Referrer
            ? values.referrerCompany || null
            : null,
        referrerMore:
          values.hearTenet === HearAboutTenetVariants.Other
            ? values.referrerInformation || null
            : null,
        applicantRelations: filledRelationships.length
          ? filledRelationships.map((e) => ({
              id: e.id.includes('custom-') ? null : e.id,
              relationshipTemplateId: e.template || '',
              additionalFields: e.additionalFields.map((af) => ({
                id: af.id,
                name: af.name,
                type: af.type,
                valueJSON: formatToJSON(af.type, af.value),
              })),
            }))
          : null,
      },
      undefined,
    );
    switch (values.submitType) {
      case 'save-continue':
        navigateToNextStep();
        break;
      case 'back':
        navigateToPreviousStep();
        break;
    }
  };

  return (
    <>
      <SectionIntro
        hideDivider
        titleColor={colorsTheme.colorWhite}
        titleVariant={disabled ? 'h5' : undefined}
        title={
          <DivAlignCenter>
            {t('intake_form.basic_info.title')}
            {renderNavigateButtonInTitle ? (
              <SectionTitleNavigateButton navigateToStepKey="basic_info" />
            ) : null}
          </DivAlignCenter>
        }
      />
      <Form
        disabled={disabled}
        onSubmit={handleSubmit}
        renderForm={<InnerForm />}
        initialValues={initialValues}
        validationSchema={basicInfoStepValidationRules}
      />
    </>
  );
};

export default BasicInfo;
